import * as React from "react";
import { useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector, useStore } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { select } from "../state/selectionSlice";
import { findBorderOnClick, isMouseClickedOnBorder } from "../helpers/selection";

const SeparatorCell = (props) => {
  const { rowIndex, colIndex, isVertical } = props;
  const dispatch = useDispatch();
  const store = useStore();
  const separatorRef = useRef(null);

  // Per-instance memoized selector returning ONLY this separator's background
  // string. createSelector recomputes when spacerStyles/cellStyles/cells change,
  // but useSelector compares the resulting string, so an unrelated cell's style
  // edit (or a border hover) no longer re-renders every separator.
  const selectBackground = useMemo(
    () =>
      createSelector(
        [
          (state) => state.gridd.present.spacerStyles,
          (state) => state.gridd.present.cellStyles,
          (state) => state.gridd.present.structure.cells,
        ],
        (spacerStyles, cellStyles, cells) => {
          const spacerColor = isVertical
            ? spacerStyles?.columnColors
              ? spacerStyles.columnColors[colIndex]
              : "white"
            : spacerStyles?.rowColors
              ? spacerStyles.rowColors[rowIndex]
              : "white";

          if (spacerColor && spacerColor !== "transparent") {
            return spacerColor;
          }

          // Background shared by both neighbors (and not "none") bleeds across the
          // separator; otherwise it stays transparent.
          const sharedBackground = (cellA, cellB) => {
            if (!cellA || !cellB) return null;
            const a = cellStyles[cellA.id]?.background || "none";
            const b = cellStyles[cellB.id]?.background || "none";
            return a === b && a !== "none" ? cellStyles[cellA.id]?.background : null;
          };

          let automaticBackground = null;
          if (isVertical) {
            const left = colIndex - 1;
            const right = colIndex + 1;
            if (left >= 0 && right < (cells[rowIndex] || []).length) {
              automaticBackground = sharedBackground(cells[rowIndex][left], cells[rowIndex][right]);
            }
          } else {
            const up = rowIndex - 1;
            const down = rowIndex + 1;
            if (up >= 0 && down < (cells || []).length) {
              automaticBackground = sharedBackground(cells[up][colIndex], cells[down][colIndex]);
            }
          }

          return automaticBackground ?? "transparent";
        }
      ),
    [rowIndex, colIndex, isVertical]
  );

  const background = useSelector(selectBackground);

  return (
    <div
      id={`spacer_${props.x}_${props.y}`}
      ref={separatorRef}
      style={{
        width: "100%",
        height: "100%",
        background,
      }}
      onClick={(e) => {
        // borders is only needed here; read at click time instead of subscribing.
        const borders = store.getState().gridd.present.borders;
        const borderFound = findBorderOnClick(isMouseClickedOnBorder(e, separatorRef.current), borders, props);
        if (borderFound) {
          dispatch(select({ border: borderFound }));
          return;
        }

        dispatch(
          select({
            row: isVertical ? undefined : rowIndex,
            column: isVertical ? colIndex : undefined,
            type: "clear",
          })
        );
      }}
    ></div>
  );
};

SeparatorCell.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  colIndex: PropTypes.number,
  rowIndex: PropTypes.number,
  id: PropTypes.string.isRequired,
  isVertical: PropTypes.bool,
};

export default React.memo(SeparatorCell);
